import { useState } from "react";

import type { Meter } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Alert, Button, Drawer, Field, Slider, Switch } from "@eisox/design-system";
import { FieldErrors, useAppForm } from "@eisox/form";
import { SwapIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { revalidateLogic } from "@tanstack/react-form";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { buildingApi } from "~/api";
import { toDayRangeIn } from "~/helpers/day-range.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { COMPARISONS, OBJECTIVE_PERCENT } from "../consumption.constant";
import { comparisonFormSchema, comparisonSearchPatch } from "../helpers/comparison.helper";
import type { ConsumptionSearch } from "../helpers/consumption-search.helper";
import { supportsDegreeDays } from "../helpers/energy.helper";
import { Comparison, type ConsumptionPeriod } from "../types/consumption.type";
import { ActivationLabel } from "./activation-label";

const NAME = "ComparisonDrawer";

interface ComparisonDrawerProps {
  values: Pick<ConsumptionSearch, "compare" | "degreeDays" | "objective">;
  meter: Meter;
  period: ConsumptionPeriod;
  today: string;
  onApply: (search: Partial<ConsumptionSearch>) => void;
}

const ComparisonDrawer: React.FC<ComparisonDrawerProps> = ({ values, meter, period, today, onApply }) => {
  const [open, setOpen] = useState(false);
  const { building, permissions } = useBuildingContext(NAME);
  const { data: weatherStations } = useSuspenseQuery(buildingApi.queries.weatherStations(building.id));
  const station = weatherStations.find(({ id }) => id === building.weatherStationId);
  const reference = building.referencePeriod && toDayRangeIn(building.referencePeriod, building.timezone);
  const canEditClimate = Boolean(
    permissions.building?.weatherStationId?.update || permissions.building?.referencePeriod?.update,
  );
  const stationInfo = station && m.consumption_weather_station_info({ station: station.name });
  const referenceInfo =
    reference &&
    m.consumption_reference_period_info({
      period: m.from_to_range({ from: dayjs(reference.from).format("L"), to: dayjs(reference.to).format("L") }),
    });
  const hasDegreeDays = supportsDegreeDays(meter);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const form = useAppForm({
    defaultValues: {
      compare: values.compare,
      degreeDays: hasDegreeDays && values.degreeDays,
      objective: values.objective,
    },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: comparisonFormSchema(period, building, today) },
    onSubmit: ({ value }) => {
      onApply(comparisonSearchPatch(value, period, building, today));
      handleOpenChange(false);
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    form.reset();
    setOpen(nextOpen);
  };

  return (
    <Drawer.Root open={open} onOpenChange={handleOpenChange}>
      <Drawer.Trigger asChild>
        <Button>
          {m.consumption_compare()}
          <SwapIcon />
        </Button>
      </Drawer.Trigger>
      <Drawer.Content>
        <Drawer.Header>{m.consumption_compare()}</Drawer.Header>
        <form
          className="min-h-0 gap-5 p-2.5 md:gap-10 md:p-10 flex flex-1 flex-col overflow-y-auto"
          onSubmit={event => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Drawer.Group>
              <Drawer.Label>{m.consumption_comparison()}</Drawer.Label>
              <Alert severity="help">{m.consumption_comparison_help()}</Alert>
              <form.AppField name="compare">
                {field => (
                  <field.Select
                    multiple={false}
                    options={Object.values(Comparison).map(comparison => ({
                      value: comparison,
                      name: COMPARISONS[comparison].label(),
                    }))}
                    classNames={{ control: "w-full" }}
                  />
                )}
              </form.AppField>
            </Drawer.Group>
            {hasDegreeDays && (
              <Drawer.Group>
                <Drawer.Label>{m.consumption_dju_correction()}</Drawer.Label>
                <Alert severity="help">
                  <p>
                    {m.consumption_dju_help()}
                    {stationInfo && `\n${stationInfo}`}
                    {referenceInfo && `\n${referenceInfo}`}
                    {canEditClimate && !isMobile && (
                      <>
                        {"\n"}
                        <Button asChild>
                          <Link
                            to="/buildings/$buildingId/settings/buildings/general"
                            params={{ buildingId: building.id }}
                          >
                            {m.consumption_building_settings()}
                          </Link>
                        </Button>
                      </>
                    )}
                  </p>
                </Alert>
                <form.AppField name="degreeDays">
                  {field => (
                    <Field.Root>
                      <ActivationLabel active={field.state.value}>
                        <Switch
                          checked={field.state.value}
                          onCheckedChange={checked => field.handleChange(checked ?? false)}
                        />
                      </ActivationLabel>
                      <FieldErrors />
                    </Field.Root>
                  )}
                </form.AppField>
              </Drawer.Group>
            )}
            <form.Subscribe selector={state => COMPARISONS[state.values.compare].objective}>
              {hasObjective => (
                <Drawer.Group>
                  <Drawer.Label>{m.consumption_target()}</Drawer.Label>
                  <Alert severity="help">{m.consumption_target_help()}</Alert>
                  <form.AppField name="objective">
                    {field => {
                      const objective = field.state.value;
                      const active = hasObjective && objective !== undefined;
                      return (
                        <Field.Root className="gap-2.5">
                          <ActivationLabel active={active}>
                            <Switch
                              checked={active}
                              disabled={!hasObjective}
                              onCheckedChange={checked =>
                                field.handleChange(checked ? OBJECTIVE_PERCENT.default : undefined)
                              }
                            />
                          </ActivationLabel>
                          {active && (
                            <Slider
                              className="w-full"
                              label={m.consumption_target_reduction({ percent: objective })}
                              value={objective}
                              onValueChange={field.handleChange}
                              min={OBJECTIVE_PERCENT.min}
                              max={OBJECTIVE_PERCENT.max}
                              step={OBJECTIVE_PERCENT.step}
                              displayButtons={false}
                              displayValue={false}
                            />
                          )}
                          <FieldErrors />
                        </Field.Root>
                      );
                    }}
                  </form.AppField>
                </Drawer.Group>
              )}
            </form.Subscribe>
            <form.SubmitButton className="mt-auto self-center" label={m.apply()} />
          </form.AppForm>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
};

ComparisonDrawer.displayName = NAME;

export { ComparisonDrawer };
